<template>
  <section class="share">
    <div class="share__inner">
      <AppButton
        ref="buttonRef"
        :title="state.label"
        :lead-icon="state.icon"
        @click="onClick"
      />
    </div>
  </section>
</template>

<script setup lang="ts">

import { reactive, ref } from 'vue'
import { copyToClipboard } from '~/utils/clipboard'
import { usePageStore } from '~/store/page'
import { TPage, TSeoMixin } from '~/types/page-types'
import { useSettingsStore } from '~/store/settings'
import { useBrowserSupportStore } from '~/store/browser-support'

const buttonRef = ref<HTMLElement>()
const pageStore = usePageStore()
const browserSupportStore = useBrowserSupportStore()
const settingsStore = useSettingsStore()
const timeout = ref()

const labels = {
  copy: 'Kopieer link',
  copied: 'Link gekopieerd!',
  share: 'Deel pagina'
}

type TShareState = {
  nativeSupported: Boolean,
  label: String,
  icon: String
}

const state: TShareState = reactive({
  nativeSupported: browserSupportStore.isNativeShareSupported,
  label: (browserSupportStore.isNativeShareSupported) ? labels.share : labels.copy,
  icon: (browserSupportStore.isNativeShareSupported) ? 'send' : 'link'
})

// listen for browser support update, this should never trigger
browserSupportStore.$subscribe(() => {
  if (state.nativeSupported !== browserSupportStore.isNativeShareSupported) {
    state.nativeSupported = browserSupportStore.isNativeShareSupported
    state.label = (browserSupportStore.isNativeShareSupported) ? labels.share : labels.copy
    state.icon = (browserSupportStore.isNativeShareSupported) ? 'send' : 'link'
  }
})

// revert to original text after 5 secs
const resetCopyLink = () => {
  if (timeout.value) {
    window.clearTimeout(timeout.value)
  }
  timeout.value = window.setTimeout(() => {
    state.label = labels.copy
  }, 5000)
}

const onClick = async () => {
  if (!state.nativeSupported && process.client) {
    // copy to clipboard
    const copied = await copyToClipboard(window.location.href)

    if (copied && buttonRef.value) {
      state.label = labels.copied
    }

    resetCopyLink()
  } else {
    // show native share (requires HTTPS, so doesn't work locally)
    const curPage = pageStore.getCurrentPage as TPage
    const seoData = curPage.seo as TSeoMixin

    const data = {
      title: seoData.search_title || curPage.title || settingsStore.getSeo.title || 'Error',
      description: seoData?.search_description || settingsStore.getSeo.description || 'Error',
      url: window.location.href
    }

    await navigator.share(data)
  }
}

</script>

<style scoped lang="postcss">
.share {
  @mixin container;
  @mixin app-container-padding;
  @mixin app-grid;

  &__inner {
    grid-column: 1 / -1;
    display: flex;
    justify-content: center;
    padding-bottom: 2rem;
  }
}
</style>
